import { ScriptIdentifier } from "@/types/campaigns";
import type { UnavailabilityPeriodRequest } from "@/types/org";
import { InboundRule, InboundRulesSimple } from "@/types/scripts";

export type ScriptStatus = "archived" | "draft" | "active";
export type ScriptDisplayLabel = "default" | "fallback" | null;

export type HolidayPeriodRef = Pick<UnavailabilityPeriodRequest, "scriptId">;

export type ScriptWithStatus = ScriptIdentifier & {
  scriptStatus: ScriptStatus;
  isDefault: boolean;
  hasRule: boolean;
  rule: InboundRule | null;
  displayLabel: ScriptDisplayLabel;
  isHoliday: boolean;
};

export function isCatchAllInboundScript(
  script: Pick<ScriptWithStatus, "isDefault" | "displayLabel">
): boolean {
  return (
    script.isDefault || script.displayLabel === "default" || script.displayLabel === "fallback"
  );
}

export function canAssignInboundHours(
  script: Pick<ScriptWithStatus, "isDefault" | "displayLabel" | "scriptStatus">,
  canUpdate: boolean
): boolean {
  if (isCatchAllInboundScript(script)) return false;
  return script.scriptStatus !== "archived" && canUpdate;
}

export function canSetInboundDefault(
  script: Pick<
    ScriptWithStatus,
    "isDefault" | "displayLabel" | "scriptStatus" | "revisionState"
  >,
  opts: {canUpdate: boolean; versioning: boolean}
): boolean {
  if (!canAssignInboundHours(script, opts.canUpdate)) return false;
  if (opts.versioning && script.revisionState === "draft") return false;
  return true;
}

export function canSetInboundFallback(
  script: Pick<
    ScriptWithStatus,
    "isDefault" | "displayLabel" | "scriptStatus" | "revisionState"
  >,
  opts: {canUpdate: boolean; versioning: boolean}
): boolean {
  if (script.scriptStatus === "archived" || !opts.canUpdate) return false;
  if (script.isDefault || script.displayLabel === "fallback") return false;
  if (opts.versioning && script.revisionState === "draft") return false;
  return true;
}

/**
 * True when this script is the org default holiday script or is assigned
 * to any unavailability period.
 */
export function isHolidayScriptAssignment(
  scriptId: string,
  rules: InboundRulesSimple | undefined,
  periods?: ReadonlyArray<HolidayPeriodRef>
): boolean {
  if (rules?.defaultHolidayScriptId === scriptId) {
    return true;
  }

  return periods?.some((period) => period.scriptId === scriptId) ?? false;
}

/**
 * Checks if a rule is an "any/any" rule (all days, all times).
 */
export function isAnyAnyRule(rule: InboundRule | null): boolean {
  if (!rule) return false;
  return rule.timeFrame === "any" && rule.hours === "any";
}

/** Existing per-phone Default (any/any) on this number, excluding `scriptId`. */
export function findAnyAnyRuleForPhone(
  rules: ReadonlyArray<InboundRule>,
  phone: string | undefined,
  defaultPhoneNumber: string | undefined,
  excludeScriptId?: string
): InboundRule | undefined {
  const targetPhone = phone || defaultPhoneNumber;
  return rules.find(
    (rule) =>
      rule.scriptId !== excludeScriptId &&
      isAnyAnyRule(rule) &&
      (rule.phoneNumber || defaultPhoneNumber) === targetPhone
  );
}

/**
 * Gets the display label for a script based on rules and phone number count.
 *
 * Logic:
 * - If multiple phone numbers AND script has any/any rule → "default"
 * - If multiple phone numbers AND script is defaultScriptId → "fallback"
 * - If single phone number AND script is defaultScriptId → "default"
 * - Otherwise → null
 */
export function getDisplayLabel(
  scriptId: string,
  rules: InboundRulesSimple | undefined,
  hasMultiplePhoneNumbers: boolean
): ScriptDisplayLabel {
  const isDefaultScript = rules?.defaultScriptId === scriptId;
  const rule = rules?.rules.find((r) => r.scriptId === scriptId) ?? null;
  const hasAnyAnyRule = isAnyAnyRule(rule);

  if (hasMultiplePhoneNumbers) {
    if (hasAnyAnyRule) return "default";
    if (isDefaultScript) return "fallback";
  } else if (isDefaultScript) {
    return "default";
  }
  return null;
}

/**
 * Determines the script status based on script status and rule associations.
 *
 * Status logic:
 * - "archived": Script has status "archived"
 * - "active": Script is associated with a rule, is the default script,
 *   or is assigned as a holiday script
 * - "draft": Script is not archived and not associated with any rule/default/holiday
 */
export function getScriptStatus(
  script: ScriptIdentifier,
  rules: InboundRulesSimple | undefined,
  periods?: ReadonlyArray<HolidayPeriodRef>
): ScriptStatus {
  if (script.status === "archived") {
    return "archived";
  }

  if (rules?.defaultScriptId === script.id) {
    return "active";
  }

  const hasRule = rules?.rules.some((rule) => rule.scriptId === script.id) ?? false;
  if (hasRule) {
    return "active";
  }

  if (isHolidayScriptAssignment(script.id, rules, periods)) {
    return "active";
  }

  return "draft";
}

/**
 * Enriches a script with status information.
 */
export function enrichScriptWithStatus(
  script: ScriptIdentifier,
  rules: InboundRulesSimple | undefined,
  hasMultiplePhoneNumbers: boolean = false,
  periods?: ReadonlyArray<HolidayPeriodRef>
): ScriptWithStatus {
  const isDefault = rules?.defaultScriptId === script.id;
  const rule = rules?.rules.find((r) => r.scriptId === script.id) ?? null;
  const hasRule = rule !== null;
  const displayLabel = getDisplayLabel(script.id, rules, hasMultiplePhoneNumbers);
  const isHoliday = isHolidayScriptAssignment(script.id, rules, periods);

  return {
    ...script,
    scriptStatus: getScriptStatus(script, rules, periods),
    isDefault,
    hasRule,
    rule,
    displayLabel,
    isHoliday,
  };
}

/**
 * Enriches multiple scripts with status information.
 */
export function enrichScriptsWithStatus(
  scripts: ScriptIdentifier[],
  rules: InboundRulesSimple | undefined,
  hasMultiplePhoneNumbers: boolean = false,
  periods?: ReadonlyArray<HolidayPeriodRef>
): ScriptWithStatus[] {
  return scripts.map((script) =>
    enrichScriptWithStatus(script, rules, hasMultiplePhoneNumbers, periods)
  );
}

/**
 * Filters scripts by status. Accepts a single status, "all", or an array of statuses.
 */
export function filterScriptsByStatus(
  scripts: ScriptWithStatus[],
  status: ScriptStatus | "all" | ScriptStatus[]
): ScriptWithStatus[] {
  if (status === "all") {
    return scripts;
  }
  if (Array.isArray(status)) {
    return scripts.filter((script) => status.includes(script.scriptStatus));
  }
  return scripts.filter((script) => script.scriptStatus === status);
}

/**
 * Gets the count of scripts for each status.
 */
export function getScriptStatusCounts(
  scripts: ScriptWithStatus[]
): Record<ScriptStatus | "all", number> {
  const counts: Record<ScriptStatus | "all", number> = {
    all: scripts.length,
    archived: 0,
    draft: 0,
    active: 0,
  };

  for (const script of scripts) {
    counts[script.scriptStatus]++;
  }

  return counts;
}

/**
 * Puts org-wide fallback scripts first in list views.
 */
export function sortScriptsWithFallbackFirst(
  scripts: ScriptWithStatus[]
): ScriptWithStatus[] {
  const fallbackScripts = scripts.filter((script) => script.displayLabel === "fallback");
  const otherScripts = scripts.filter((script) => script.displayLabel !== "fallback");

  return [...fallbackScripts, ...otherScripts];
}

/**
 * Filters scripts by phone number.
 * Returns all scripts if phoneNumber is "all" or empty.
 * Org-wide fallback scripts are always included and sorted to the top.
 * Other matches are scripts with a rule for the specified phone number,
 * or scripts without a phone-specific rule when filtering by the default phone.
 */
export function filterScriptsByPhoneNumber(
  scripts: ScriptWithStatus[],
  phoneNumber: string,
  defaultPhoneNumber?: string
): ScriptWithStatus[] {
  if (phoneNumber === "all" || !phoneNumber) {
    return sortScriptsWithFallbackFirst(scripts);
  }

  const filtered = scripts.filter((script) => {
    if (script.displayLabel === "fallback") {
      return true;
    }

    if (script.rule?.phoneNumber) {
      return script.rule.phoneNumber === phoneNumber;
    }

    if (script.isDefault || !script.rule?.phoneNumber) {
      return phoneNumber === defaultPhoneNumber;
    }

    return false;
  });

  return sortScriptsWithFallbackFirst(filtered);
}
